import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Droplet, Activity, Waves, ShieldCheck, Eye } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SeoHead } from "@/components/SeoHead";
import { staggerContainer, fadeUp } from "@/lib/motion";
import { useContent } from "@/context/ContentContext";

const PARAM_ICONS = [Droplet, Activity, Waves, ShieldCheck, Eye];

export default function Teknoloji() {
  const { t } = useTranslation();
  const { media } = useContent();
  const params = t("tech.params", { returnObjects: true });

  return (
    <div data-testid="teknoloji-page" className="py-20 md:py-28">
      <SeoHead pageKey="tech" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* HEADER */}
        <Reveal>
          <p className="text-sm tracking-widest uppercase text-[#2b8a7e] mb-5">
            {t("tech.overline")}
          </p>
          <h1 className="font-display font-bold text-[#1a3636] text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-none">
            {t("tech.title")}
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#1a3636]/65 leading-relaxed max-w-2xl">
            {t("tech.sub")}
          </p>
        </Reveal>

        {/* ARCHITECTURE DIAGRAM */}
        <Reveal className="mt-14" delay={0.1}>
          {/* ==== SYSTEM ARCHITECTURE DIAGRAM IMAGE ==== */}
          <div className="relative rounded-2xl overflow-hidden border-4 border-white shadow-[0_20px_40px_rgb(26,54,54,0.12)] bg-[#1a3636]">
            <img
              src={media.systemDiagram}
              alt={t("tech.diagramLabel")}
              data-testid="tech-architecture-image"
              className="w-full h-auto object-contain"
            />
            <span className="absolute top-4 left-4 bg-[#f8faf9]/90 backdrop-blur px-4 py-1.5 rounded-full text-xs uppercase tracking-widest text-[#1a3636]">
              {t("tech.diagramLabel")}
            </span>
          </div>
        </Reveal>
      </div>

      {/* PARAMETERS */}
      <section className="mt-24 md:mt-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display font-bold text-[#1a3636] text-3xl md:text-4xl tracking-tight max-w-2xl">
              {t("tech.paramsTitle")}
            </h2>
          </Reveal>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {params.map((p, i) => {
              const Icon = PARAM_ICONS[i];
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`tech-param-${i}`}
                  className={`group bg-white rounded-2xl p-8 border border-[#1a3636]/5 shadow-[0_8px_30px_rgb(26,54,54,0.06)] hover:shadow-[0_20px_40px_rgb(43,138,126,0.12)] transition-all duration-500 ${
                    i === 4 ? "lg:col-span-1 md:col-span-2" : ""
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#2b8a7e]/10 flex items-center justify-center text-[#2b8a7e] mb-6 group-hover:scale-110 transition-transform duration-500">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#1a3636] tracking-tight mb-2">
                    {p.name}
                  </h3>
                  <p className="text-[#1a3636]/60 leading-relaxed text-sm">{p.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* HIGHLIGHT METRIC */}
      <section className="mt-24 md:mt-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="relative overflow-hidden rounded-[2rem] bg-[#1a3636] px-8 md:px-16 py-20 md:py-28 text-center">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-[#2b8a7e]/20 blur-3xl" />
            <div className="relative">
              <motion.p
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="font-display font-bold text-[#2b8a7e] text-7xl md:text-9xl tracking-tighter leading-none"
                data-testid="tech-highlight-metric"
              >
                {t("tech.highlight")}
              </motion.p>
              <p className="mt-4 font-display text-2xl md:text-3xl font-medium text-[#f8faf9] tracking-tight">
                {t("tech.highlightLabel")}
              </p>
              <p className="mt-5 text-[#f8faf9]/60 leading-relaxed max-w-xl mx-auto">
                {t("tech.highlightDesc")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
